// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Button } from "@/components/Button"
import { Link } from "@/components/Anchor"

// Components
import { VoucherInstructions } from "./_components/VoucherInstructions"
import { AvailableMemberGetMemberConversions } from "./_components/AvailableMemberGetMemberConversions"

// i18n
import { useTrans } from "@/i18n"
import { useLang } from "@/context/lang"

// Utils
import { copy } from "@/lib/copyClipboard"

// Context
import { useToasts } from "@/context/toasts"

// Queries
import { useMemberGetMemberVoucherQuery } from "@/api/graphql"

// Icons
import { FiCopy } from "@/lib/icons"

// Environment variables
import { EXTERNAL_PLATFORM_ZONHUB_INVESTMENT } from "@/lib/env"

/**
 * CommunitySharePage
 * @returns
 */
export function CommunitySharePage() {
	// Translations
	const t = useTrans("investor")
	const { formatCurrency } = useLang()

	// Context
	const toasts = useToasts()

	// Campaign info
	const { data } = useMemberGetMemberVoucherQuery()
	const campaignTitle = t("investor:investor.community.invites.share.title")

	// Share full message. Fallback to copy if not supported.
	async function shareFullMessage(
		code: string | null | undefined,
		discount: Number | null | undefined,
	) {
		const campaignText = t(
			"investor:investor.community.invites.share.text",
			{
				code: code,
				discount: discount,
			},
		)

		if (navigator.share) {
			try {
				await navigator.share({
					title: campaignTitle,
					text: campaignText,
					url: EXTERNAL_PLATFORM_ZONHUB_INVESTMENT,
				})

				// Show toast
				toasts.addToast({
					text: t("investor:investor.community.invites.share.thanks"),
					id: "share-community-share",
				})
			} catch (_error) {
				void shareFullMessageViaCopy(
					String(
						data?.me?.member_get_member_voucher?.code,
					).toUpperCase(),
					data?.me?.member_get_member_voucher?.amount,
				)
			}
		} else {
			void shareFullMessageViaCopy(
				String(data?.me?.member_get_member_voucher?.code).toUpperCase(),
				data?.me?.member_get_member_voucher?.amount,
			)
		}
	}

	/**
	 * copyOnlyCodeViaClipboard
	 */
	const copyOnlyCodeViaClipboard = async (
		code: string | null | undefined,
	) => {
		try {
			await copy(`${code}`)
			toasts.addToast({
				text: t("investor:investor.community.invites.share.success"),
				id: "copy-community-share",
			})
		} catch {
			toasts.addToast({
				text: t("investor:investor.community.invites.share.failed"),
				id: "copy-recovery-codes-failed",
			})
		}
	}

	// shareFullMessageViaCopy
	const shareFullMessageViaCopy = async (
		code: string | null | undefined,
		discount: number | null | undefined,
	) => {
		const campaignText = t(
			"investor:investor.community.invites.share.text",
			{
				code: code,
				discount: discount,
			},
		)

		try {
			await copy(
				`${campaignTitle}\n\n${campaignText}\n\n${EXTERNAL_PLATFORM_ZONHUB_INVESTMENT}`,
			)
			toasts.addToast({
				text: t("investor:investor.community.invites.share.success"),
				id: "copy-community-share",
			})
		} catch {
			toasts.addToast({
				text: t("investor:investor.community.invites.share.failed"),
				id: "copy-recovery-codes-failed",
			})
		}
	}

	return (
		<>
			<CardWrapper>
				<CardBody className="col flex gap-5">
					{data?.me?.member_get_member_voucher ? (
						<div className="max-w-lg">
							<div className="relative mb-14">
								<div className="absolute top-10 flex w-full flex-row justify-center">
									<div className="rounded-lg bg-white p-2 text-center">
										{String(
											data?.me?.member_get_member_voucher
												?.code,
										).toUpperCase()}
										<Button
											variant="transparent"
											size="small"
											className="ml-2"
											onClick={() =>
												void copyOnlyCodeViaClipboard(
													String(
														data?.me
															?.member_get_member_voucher
															?.code,
													).toUpperCase(),
												)
											}
										>
											<FiCopy />
										</Button>
									</div>
								</div>

								<img
									src="/assets/sharing/member-get-member.png"
									alt="Network Invites"
									className="mt-4"
								/>
								<div className="absolute -bottom-10 flex w-full justify-center">
									<div className="flex size-20 items-center justify-center rounded-full bg-primary-500 text-black">
										<span className="text-center text-2xl font-bold">
											{formatCurrency(
												Number(
													data?.me
														?.member_get_member_voucher
														?.amount,
												),
												{
													maximumFractionDigits: 0,
												},
											)}
										</span>
									</div>
								</div>
							</div>

							<p className="mt-5 text-center">
								{t("investor:investor.community.invites.copy", {
									discount:
										data?.me?.member_get_member_voucher
											.amount,
								})}
							</p>
							<div className="flex justify-center">
								<Button
									onClick={() =>
										shareFullMessage(
											data?.me?.member_get_member_voucher
												?.code,
											data?.me?.member_get_member_voucher
												?.amount,
										)
									}
									className="my-5"
								>
									{t(
										"investor:investor.community.invites.button",
									)}
								</Button>
							</div>
							<br />
							<div className="flex justify-center">
								<Link href="#">
									{t(
										"investor:investor.community.invites.terms",
									)}
								</Link>
							</div>
						</div>
					) : null}

					<VoucherInstructions />
				</CardBody>
			</CardWrapper>

			{/* Available Claims Table */}
			<AvailableMemberGetMemberConversions />
		</>
	)
}
